import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subscription } from 'rxjs';
import { ProctoringService, ViolationState } from '../../services/proctoring.service';
import { ProctoringViolationType, ViolationSeverity } from '../../models/proctoring.models';

@Component({
  selector: 'app-proctoring-warning',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './proctoring-warning.component.html',
  styleUrl: './proctoring-warning.component.css'
})
export class ProctoringWarningComponent implements OnInit, OnDestroy {
  state: ViolationState = { warningCount: 0, criticalCount: 0, isTerminated: false, currentWarning: null, terminationReason: null };
  private sub?: Subscription;
  private dismissTimeout?: any;

  constructor(private proctoringService: ProctoringService, private cdr: ChangeDetectorRef) { }

  ngOnInit() {
    this.sub = this.proctoringService.violationState$.subscribe(state => {
      if (state.currentWarning && state.currentWarning !== this.state.currentWarning) {
        this.startAutoDismissTimer();
      } else if (!state.currentWarning) {
        this.clearAutoDismissTimer();
      }
      this.state = state;
      this.cdr.detectChanges();
    });
  }

  private startAutoDismissTimer() {
    this.clearAutoDismissTimer();
    this.dismissTimeout = setTimeout(() => {
      this.dismiss();
    }, 5000);
  }

  private clearAutoDismissTimer() {
    if (this.dismissTimeout) {
      clearTimeout(this.dismissTimeout);
      this.dismissTimeout = undefined;
    }
  }

  isCritical(): boolean {
    const w = this.state.currentWarning;
    // Soft-nudge types — only EYES_CLOSED and FACE_TOO_FAR remain non-critical
    if (
      w === ProctoringViolationType.EYES_CLOSED ||
      w === ProctoringViolationType.FACE_TOO_FAR ||
      w === ProctoringViolationType.BACKGROUND_VOICE ||
      w === ProctoringViolationType.NO_FACE
    ) {
      return false;
    }
    return true;
  }

  getWarningIcon(): string {
    switch (this.state.currentWarning) {
      case ProctoringViolationType.NO_FACE: return 'bi-person-bounding-box';
      case ProctoringViolationType.MULTIPLE_FACES: return 'bi-people-fill';
      case ProctoringViolationType.LOOKING_AWAY: return 'bi-eye-slash-fill';
      case ProctoringViolationType.TAB_SWITCH: return 'bi-window-stack';
      case ProctoringViolationType.EYES_CLOSED: return 'bi-eye-fill';
      case ProctoringViolationType.FACE_TOO_FAR: return 'bi-arrows-angle-expand';
      case ProctoringViolationType.CAMERA_TURNED_OFF: return 'bi-camera-video-off-fill';
      default: return 'bi-exclamation-triangle-fill';
    }
  }

  dismiss() {
    this.clearAutoDismissTimer();
    if (this.state.currentWarning) {
      this.proctoringService.dismissWarning(String(this.state.currentWarning));
    }
  }

  getWarningTitle(): string | null {
    const warning = String(this.state.currentWarning || '').toUpperCase();

    switch (warning) {
      case 'NO_FACE': return 'Face Not Detected';
      case 'MULTIPLE_FACES': return 'Multiple Faces Detected';
      case 'LOOKING_AWAY': return 'Look Forward';
      case 'TAB_SWITCH': return 'Tab Switch Detected';
      case 'EYES_CLOSED': return 'Eyes Not Visible';
      case 'FACE_TOO_FAR': return 'Posturing Issue';
      case 'CAMERA_TURNED_OFF': return 'Camera Disabled';
      case 'BACKGROUND_VOICE': return 'Background Audio Detected';
      case 'MULTIPLE_VOICE': return 'Multiple Voices Detected';
      default:
        return null;
    }
  }

  hasValidWarning(): boolean {
    return !!this.getWarningTitle();
  }

  getWarningMessage(): string {
    const warning = String(this.state.currentWarning || '').toUpperCase();

    switch (warning) {
      case 'NO_FACE': return 'Please ensure your face is clearly visible in the camera frame.';
      case 'MULTIPLE_FACES': return 'Only you should be visible in the camera frame.';
      case 'LOOKING_AWAY': return 'Please keep your eyes focused on the screen.';
      case 'TAB_SWITCH': return 'Please stay on the interview tab. Switching away is a violation.';
      case 'EYES_CLOSED': return 'Please ensure your eyes are visible and focused on the screen.';
      case 'FACE_TOO_FAR': return 'Please adjust your distance and posture to stay in frame.';
      case 'CAMERA_TURNED_OFF': return 'Your camera appears to be disabled. Please re-enable it.';
      case 'BACKGROUND_VOICE': return 'Unexpected background audio detected. Please ensure a quiet environment.';
      case 'MULTIPLE_VOICE': return 'Multiple voices detected. Only the candidate should be speaking.';
      default: return 'Please correct your posture and stay in frame.';
    }
  }

  getMaxWarningsArray(): number[] {
    return Array(this.state.maxWarnings || 5).fill(0).map((_, i) => i + 1);
  }

  ngOnDestroy() {
    this.clearAutoDismissTimer();
    this.sub?.unsubscribe();
  }
}
